import {
  Box, Drawer, List, ListItem, ListItemButton, ListItemIcon, ListItemText,
  Typography, Divider, Avatar, useTheme,
} from '@mui/material';
import {
  Dashboard, PointOfSale, Inventory, ShoppingCart, People, LocalShipping,
  Receipt, BarChart, Settings, Category, AccountBalance, AttachMoney,
  MoveToInbox, Store, Factory, LocalHospital, SupportAgent, BusinessCenter, Person,
  QrCode, AccessTime, RequestQuote, LocalOffer, FactCheck,
  EventBusy, Autorenew, DeleteForever,
} from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

const DRAWER_WIDTH = 260;

// module: which modules key must be true in tenant modules (undefined = always show)
// roles: restrict to specific roles
// moduleAny: for dividers — only show if at least one of these modules is active
const navItems = [
  // ── Core ─────────────────────────────────────────────────────────────────
  { label: 'Dashboard',          icon: <Dashboard />,       path: '/dashboard' },
  { label: 'POS / Sale',         icon: <PointOfSale />,     path: '/pos' },
  { label: 'Sales History',      icon: <Receipt />,         path: '/sales' },
  { divider: true },

  // ── Products & Stock ──────────────────────────────────────────────────────
  { label: 'Products',           icon: <Inventory />,       path: '/products' },
  { label: 'Categories',         icon: <Category />,        path: '/categories' },
  { label: 'Brands',             icon: <Category />,        path: '/brands' },
  { label: 'Inventory',          icon: <Inventory />,       path: '/inventory' },
  { label: 'Purchases',          icon: <ShoppingCart />,    path: '/purchases' },
  { divider: true },

  // ── People ────────────────────────────────────────────────────────────────
  { label: 'Customers',          icon: <People />,          path: '/customers' },
  { label: 'Suppliers',          icon: <LocalShipping />,   path: '/suppliers' },
  { label: 'Ledger',             icon: <AccountBalance />,  path: '/ledger' },
  { divider: true },

  // ── Finance ───────────────────────────────────────────────────────────────
  { label: 'Expenses',           icon: <AttachMoney />,     path: '/expenses' },
  { label: 'Reports',            icon: <BarChart />,        path: '/reports' },
  { divider: true },

  // ── Tools (always visible) ────────────────────────────────────────────────
  { label: 'Stock Movements',    icon: <MoveToInbox />,     path: '/inventory/movements' },
  { label: 'Barcode Labels',     icon: <QrCode />,          path: '/barcode-labels' },
  { label: 'Shifts',             icon: <AccessTime />,      path: '/shifts' },
  { label: 'Quotations',         icon: <RequestQuote />,    path: '/quotations' },
  { label: 'Vouchers',           icon: <LocalOffer />,      path: '/vouchers' },
  { label: 'Stocktake',          icon: <FactCheck />,       path: '/stocktake' },
  { label: 'Reorder List',       icon: <Autorenew />,       path: '/inventory/reorder' },
  { label: 'Damage / Write-Off', icon: <DeleteForever />,   path: '/inventory/damage' },
  { label: 'Branches',           icon: <BusinessCenter />,  path: '/branches', roles: ['owner','super_admin'] },
  { divider: true },

  // ── Module-specific ───────────────────────────────────────────────────────
  // Medical module: enabled when modules.medical === true
  { label: 'Expiry Management',  icon: <EventBusy />,       path: '/inventory/expiry', module: 'medical' },
  { label: 'Medical Store',      icon: <LocalHospital />,   path: '/medical',          module: 'medical' },
  // Garment module
  { label: 'Production',         icon: <Factory />,         path: '/production',       module: 'garment' },
  // Divider only shown when at least one module-specific item is visible
  { divider: true, moduleAny: ['medical', 'garment'] },

  // ── Account ───────────────────────────────────────────────────────────────
  { label: 'Support',            icon: <SupportAgent />,    path: '/support' },
  { label: 'Subscription',       icon: <Store />,           path: '/settings/subscription' },
  { label: 'Users',              icon: <People />,          path: '/users',            roles: ['owner','super_admin'] },
  { label: 'Settings',           icon: <Settings />,        path: '/settings' },
  { label: 'My Profile',         icon: <Person />,          path: '/profile' },
];

export default function Sidebar({ open, topOffset = 0 }) {
  const { t } = useTranslation();
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useSelector(s => s.auth);

  // Parse modules from user (super_admin sees everything)
  const modules = user?.role === 'super_admin'
    ? { medical: true, garment: true, clothing: true }
    : (user?.modules || {});

  const isModuleEnabled = (mod) => !mod || modules[mod] === true;

  const content = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Logo */}
      <Box sx={{ p: 3, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box sx={{ width: 36, height: 36, bgcolor: 'primary.main', borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <PointOfSale sx={{ color: 'white', fontSize: 20 }} />
        </Box>
        <Box>
          <Typography variant="h6" fontWeight={700} color="primary" lineHeight={1}>POS Pro</Typography>
          {user?.business_type && user.business_type !== 'general' && (
            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'capitalize', fontSize: 10 }}>
              {user.business_type}
            </Typography>
          )}
        </Box>
      </Box>

      <Divider />

      {/* Nav */}
      <List sx={{ flex: 1, px: 1, py: 1, overflowY: 'auto' }}>
        {navItems.map((item, idx) => {
          // Handle smart dividers
          if (item.divider) {
            if (item.moduleAny) {
              const anyVisible = item.moduleAny.some(m => isModuleEnabled(m));
              if (!anyVisible) return null;
            }
            return <Divider key={idx} sx={{ my: 1 }} />;
          }

          // Role check
          if (item.roles && !item.roles.includes(user?.role)) return null;

          // Module check
          if (!isModuleEnabled(item.module)) return null;

          const active = location.pathname === item.path ||
            (item.path !== '/dashboard' && location.pathname.startsWith(item.path));
          const label = item.label;

          return (
            <ListItem key={item.path} disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton
                onClick={() => navigate(item.path)}
                sx={{
                  borderRadius: 2,
                  bgcolor: active ? 'primary.main' : 'transparent',
                  color: active ? 'white' : 'text.secondary',
                  '&:hover': { bgcolor: active ? 'primary.dark' : 'action.hover' },
                }}
              >
                <ListItemIcon sx={{ color: 'inherit', minWidth: 40 }}>{item.icon}</ListItemIcon>
                <ListItemText primary={label} primaryTypographyProps={{ fontSize: 14, fontWeight: active ? 600 : 400 }} />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>

      <Divider />

      {/* User info */}
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Avatar sx={{ width: 36, height: 36, bgcolor: 'primary.main', fontSize: 14 }}>
          {user?.name?.[0]?.toUpperCase()}
        </Avatar>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" fontWeight={600} noWrap>{user?.name}</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'capitalize' }}>{user?.role}</Typography>
        </Box>
      </Box>
    </Box>
  );

  return (
    <Drawer
      variant="persistent"
      open={open}
      sx={{
        width: open ? DRAWER_WIDTH : 0,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: DRAWER_WIDTH,
          boxSizing: 'border-box',
          border: 'none',
          boxShadow: theme.palette.mode === 'dark' ? '1px 0 0 rgba(255,255,255,0.05)' : '1px 0 0 rgba(0,0,0,0.06)',
          top: topOffset,
          height: `calc(100% - ${topOffset}px)`,
        },
      }}
    >
      {content}
    </Drawer>
  );
}
